import { Fragment, ReactNode } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { styled } from "styled-components";
import { StyledButton, OutlineOrangeButton } from "./Typography";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  onOk?: () => void;
  title?: string;
  okText?: string | ReactNode;
  cancelText?: string;
  children?: ReactNode;
  okButtonStyle?: React.CSSProperties;
}

const Overlay = styled(Transition.Child)`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 40;
`;

const DialogPanel = styled(Dialog.Panel)`
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius};
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  z-index: 50;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const Title = styled(Dialog.Title)`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.blue900};
  margin-bottom: 16px;
`;

const Content = styled.div`
  margin-bottom: 24px;
  color: ${({ theme }) => theme.colors.blue500};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

export const Modal = ({
  open,
  onClose,
  onOk,
  title,
  okText = "OK",
  cancelText = "Zrušit",
  children,
  okButtonStyle,
}: ModalProps) => {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" onClose={onClose} style={{ position: "relative", zIndex: 40 }}>
        <Overlay
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div />
        </Overlay>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel>
            {title && <Title>{title}</Title>}
            {children && <Content>{children}</Content>}
            <Footer>
              <OutlineOrangeButton onClick={onClose}>
                {cancelText}
              </OutlineOrangeButton>
              {onOk && (
                <StyledButton onClick={onOk} style={okButtonStyle}>
                  {okText}
                </StyledButton>
              )}
            </Footer>
          </DialogPanel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};
